/* ───────────────────────────────────────────────────────────────────────────
   OTG essay bridge — reads college-application essays a student submitted on
   the OTG-26 essay tool (a SEPARATE Firebase project, otg-2026-nmsquad) and
   matches them to TUAP students by NAME (never email — students use multiple
   Google accounts). READ-ONLY: never writes to OTG.
   ─────────────────────────────────────────────────────────────────────────── */

import { initializeApp } from "firebase/app";
import { getFirestore, collection, getDocs, onSnapshot } from "firebase/firestore";

// OTG-26's public web config (already public in OTG's shipped bundle).
const otgConfig = {
  apiKey: "AIzaSyBNA5NYAaC5PDhMHprMEEoqsLEnLR0918s",
  authDomain: "otg-2026-nmsquad.firebaseapp.com",
  projectId: "otg-2026-nmsquad",
  storageBucket: "otg-2026-nmsquad.firebasestorage.app",
  messagingSenderId: "442708897739",
  appId: "1:442708897739:web:6ca7e05e77d20e8c72504e",
};
// Secondary app — does not disturb the default nm-squad app.
const otgApp = initializeApp(otgConfig, "otg-essays");
const otgDb = getFirestore(otgApp);

// lowercase, trim, collapse inner spaces, strip punctuation + diacritics.
export function normName(s) {
  return String(s == null ? "" : s)
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// First/last name of an essay — newer essays carry the split fields,
// older ones only studentName (first token = first, rest = last).
export function essayNames(e) {
  if (e.studentFirstName || e.studentLastName) {
    return { first: e.studentFirstName || "", last: e.studentLastName || "" };
  }
  const p = String(e.studentName || "").trim().split(/\s+/);
  return { first: p[0] || "", last: p.slice(1).join(" ") };
}

// First/last name of a TUAP student — onboarding fields, else split studentName.
export function studentNames(student) {
  const a = student.onbAnswers || {};
  if (a.firstName || a.lastName) {
    return { first: a.firstName || "", last: a.lastName || "" };
  }
  const p = String(student.studentName || "").trim().split(/\s+/);
  return { first: p[0] || "", last: p.slice(1).join(" ") };
}

// An essay belongs to a student only if BOTH normalized names match and
// neither side is blank. First name alone is never enough.
export function essayMatchesStudent(essay, student) {
  const e = essayNames(essay), s = studentNames(student);
  const ef = normName(e.first), el = normName(e.last);
  const sf = normName(s.first), sl = normName(s.last);
  return !!ef && !!el && !!sf && !!sl && ef === sf && el === sl;
}

export async function fetchOtgEssays() {
  const snap = await getDocs(collection(otgDb, "studentEssays"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function fetchOtgFeedback() {
  const snap = await getDocs(collection(otgDb, "essayFeedback"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/* ───────────────────────────────────────────────────────────────────────────
   NEW onboarding bridge — students now fill onboarding at
   onboarding.neerajmandhana.com. The wizard mirrors NON-SENSITIVE progress
   (name + pct + done only, no answers/contacts/documents) to otg-2026-nmsquad
   `onboardingProgress/{uid}`, which is world-readable, so the FM/Rajib TUAP
   board can read it LIVE with no otg-2026 auth. The full onboardingProfiles doc
   stays locked to owner + team. READ-ONLY. Matched to a TUAP student by
   first+last name (students sign in with varied Gmails, so email is unreliable
   and is deliberately NOT in this public projection).
   ─────────────────────────────────────────────────────────────────────────── */

// Live subscription to every onboarding progress row. Returns an unsubscribe fn.
export function subscribeOnboardingProfiles(cb) {
  return onSnapshot(
    collection(otgDb, "onboardingProgress"),
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err => { console.warn("onboardingProgress subscribe error:", err.message); cb([]); }
  );
}

// First/last name of an onboarding progress row.
function profileNames(p) {
  if (p.firstName || p.lastName) return { first: p.firstName || "", last: p.lastName || "" };
  const parts = String(p.studentName || p.displayName || "").trim().split(/\s+/);
  return { first: parts[0] || "", last: parts.slice(1).join(" ") };
}

// Does this onboarding progress row belong to this TUAP student? Name match.
export function onbProfileMatchesStudent(profile, student) {
  const p = profileNames(profile), s = studentNames(student);
  const pf = normName(p.first), pl = normName(p.last);
  const sf = normName(s.first), sl = normName(s.last);
  return !!pf && !!pl && !!sf && !!sl && pf === sf && pl === sl;
}

// Live onboarding status for one student, given the current profiles array.
// Returns { started, pct, done, answeredCount, updatedAt } — started=false means
// no profile exists yet (they have not opened the new onboarding).
export function onbLiveForStudent(profiles, student) {
  const prof = (profiles || []).find(p => onbProfileMatchesStudent(p, student));
  if (!prof) return { started: false, pct: 0, done: false, answeredCount: 0, updatedAt: null };
  const done = !!prof.done;
  const pct = done ? 100 : (typeof prof.pct === "number" ? prof.pct : 0);
  return {
    started: true,
    pct,
    done,
    answeredCount: typeof prof.answeredCount === "number" ? prof.answeredCount : 0,
    updatedAt: prof.updatedAt || null,
  };
}
